import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import FormSuccess from "@/components/FormSuccess";
import { usePageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function HolidayForm() {
  usePageMeta("Holiday Package Request", "Tell us your dream holiday and our specialists will craft a tailor-made package with flights, hotels and experiences.");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", destination: sp.get("destination") || "",
    num_people: parseInt(sp.get("travellers")) || 2,
    travel_date: sp.get("departure") || "", return_date: sp.get("return") || "",
    budget: sp.get("budget") || "", hotel_preference: "", activities: "", notes: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/holiday", form);
      trackEvent("enquiry_submitted", { form: "holiday" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const reset = () => { setDone(false); setForm({ full_name: "", phone: "", email: "", destination: "", num_people: 2, travel_date: "", return_date: "", budget: "", hotel_preference: "", activities: "", notes: "" }); };

  return (
    <div className="bg-white">
      <section className="bg-[#0A2E4D] text-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <span className="mk-gold-line mb-6 block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4A62A] font-bold mb-3">Holiday Package</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl">Plan your holiday</h1>
          <p className="text-slate-300 mt-4 max-w-xl">Tell us about your dream getaway and we&apos;ll craft the perfect package.</p>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {done ? (
            <FormSuccess waMessage="Hello Makchester Tours & Travel, I would like assistance with a holiday package." onReset={reset} testId="holiday-form-success" />
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-8 space-y-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="holiday-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required><Input data-testid="holiday-full-name" value={form.full_name} onChange={set("full_name")} required /></Field>
                <Field label="Phone" required><Input data-testid="holiday-phone" value={form.phone} onChange={set("phone")} required /></Field>
                <Field label="Email"><Input type="email" data-testid="holiday-email" value={form.email} onChange={set("email")} /></Field>
                <Field label="Destination" required><Input data-testid="holiday-destination" value={form.destination} onChange={set("destination")} required /></Field>
                <Field label="Number of people"><Input type="number" min={1} data-testid="holiday-people" value={form.num_people} onChange={set("num_people")} /></Field>
                <Field label="Budget (USD)"><Input type="number" min={0} data-testid="holiday-budget" value={form.budget} onChange={set("budget")} /></Field>
                <Field label="Travel date"><Input type="date" data-testid="holiday-travel-date" value={form.travel_date} onChange={set("travel_date")} /></Field>
                <Field label="Return date"><Input type="date" data-testid="holiday-return-date" value={form.return_date} onChange={set("return_date")} /></Field>
                <Field label="Hotel preference"><Input placeholder="3-star / 4-star / 5-star / villa" data-testid="holiday-hotel" value={form.hotel_preference} onChange={set("hotel_preference")} /></Field>
                <Field label="Activities required"><Input placeholder="Safari, spa, city tours…" data-testid="holiday-activities" value={form.activities} onChange={set("activities")} /></Field>
              </div>
              <Field label="Notes"><Textarea rows={3} data-testid="holiday-notes" value={form.notes} onChange={set("notes")} /></Field>
              <button type="submit" disabled={busy}
                      className="w-full mk-btn-gold !py-4 disabled:opacity-60" data-testid="holiday-submit-btn">
                {busy ? "Submitting…" : "Get My Holiday Quote"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569] font-semibold">{label}{required && <span className="text-[#EF4444] ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
